import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DeleteButton from "../components/DeleteButton";
import EditButton from "../components/EditButton";
import {
  createStarter,
  deleteStarter,
  deleteStarterImage,
  getStarters,
  importStarters,
  reorderStarterImages,
  updateStarter,
  updateStarterVisibility,
  uploadStarterImage,
} from "../api/startersApi";

import JSZip from "jszip";
import { saveAs } from "file-saver";
import { useAuth } from "../auth/AuthContext";
import { hasPermission, isRootUser } from "../auth/permissions";
import DismissibleAlert from "../components/DismissibleAlert";
import ProductImageManager, {
  ProductImagePreview,
} from "../components/ProductImageManager";
import ProductCatalogDialog from "../components/ProductCatalogDialog";
import ProductDeleteConfirmationDialog from "../components/ProductDeleteConfirmationDialog";
import ProductCatalogColumnFilter from "../components/ProductCatalogColumnFilter";
import ProductCatalogTableControls from "../components/ProductCatalogTableControls";
import ProductCatalogSortableHeader from "../components/ProductCatalogSortableHeader";
import ProductChipListField from "../components/ProductChipListField";
import ProductCatalogEmptyRow from "../components/ProductCatalogEmptyRow";
import { PRODUCT_VISIBILITY_OPTIONS } from "../constants/productVisibility";
import { readProductCatalogTableSettings } from "../utils/productCatalogTableSettings";
import {
  ProductCatalogActionButton,
  ProductCatalogPage,
  ProductCatalogSectionHeader,
  ProductCatalogTableFrame,
  ProductCatalogToolbar,
  productCatalogFlexibleTableClassName,
} from "../components/ProductCatalogOverview";

const filledEntries = (entries) =>
  entries.map((entry) => entry.trim()).filter(Boolean);

const moveListItem = (items, index, offset) => {
  const targetIndex = index + offset;
  if (targetIndex < 0 || targetIndex >= items.length) return items;
  const next = [...items];
  [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
  return next;
};

const STARTER_COLUMNS = [
  { key: "Model", label: "Model" },
  { key: "Voltage", label: "Voltage" },
  { key: "Power", label: "Power" },
  { key: "Teeth", label: "Teeth" },
  { key: "RotationDirection", label: "Rotation" },
  { key: "OEM", label: "OEM" },
  { key: "Manufacturer", label: "Manufacturer" },
  { key: "ManufacturerIds", label: "Manufacturer IDs" },
  { key: "Lester", label: "Lester IDs" },
  { key: "Application", label: "Applications" },
  { key: "Image", label: "Image" },
];

const STARTER_COLUMN_WIDTHS = {
  Model: "min-w-28",
  Voltage: "min-w-16",
  Power: "min-w-16",
  Teeth: "min-w-14",
  RotationDirection: "min-w-24",
  OEM: "min-w-32",
  Manufacturer: "min-w-32",
  ManufacturerIds: "min-w-40",
  Lester: "min-w-32",
  Application: "min-w-48",
  Image: "min-w-24",
};

const STARTER_TABLE_SETTINGS_KEY = "starter-table-settings";

const readStarterTableSettings = () =>
  readProductCatalogTableSettings(STARTER_TABLE_SETTINGS_KEY, STARTER_COLUMNS, {
    migrateVisibleColumnKeys: (stored) => {
      const hasRotationColumn =
        Array.isArray(stored.columnOrder) &&
        stored.columnOrder.includes("RotationDirection");
      return hasRotationColumn ? [] : ["RotationDirection"];
    },
  });

const getStarterColumnValue = (item, key) => {
  if (key === "Image")
    return item.hasImage === "✅" ? "Available" : "Not uploaded";
  return item[key];
};

const toStarterPageItem = (starter) => ({
  starterId: starter.starterId,
  visibility: starter.visibility || "PUBLISHED",
  createdByUserId: starter.createdByUserId,
  imageUrl: starter.starterId
    ? starter.images?.[0]?.imageUrl
    : starter.imageUrl,
  images: (starter.images || []).map((image) => ({
    ...image,
    imageUrl: `/api/starters/${encodeURIComponent(
      starter.starterId,
    )}/images/${encodeURIComponent(image.imageId)}`,
  })),
  hasImage: starter.images?.length ? "✅" : "❌",
  Model: starter.model,
  Voltage: starter.voltage,
  Power: starter.power,
  Teeth: starter.teeth,
  RotationDirection: starter.rotationDirection ?? "CW",
  OEM: starter.oems,
  Manufacturer: starter.manufacturer,
  ManufacturerIds: starter.manufacturerIds,
  Lester: starter.lesterIds,
  Application: starter.applications,
});

const toCreateStarterRequest = (item) => {
  if (!item || typeof item !== "object" || Array.isArray(item)) {
    throw new Error("Each imported starter must be a JSON object.");
  }

  const voltage = item.voltage ?? item.Voltage ?? "";
  const power = item.power ?? item.Power ?? "";

  return {
    model: item.model ?? item.Model,
    voltage: String(voltage),
    power: String(power),
    teeth: item.teeth ?? item.Teeth ?? null,
    rotationDirection: item.rotationDirection ?? item.RotationDirection ?? "CW",
    oems: item.oems ?? item.OEM ?? [],
    manufacturer: item.manufacturer ?? item.Manufacturer ?? "",
    manufacturerIds: item.manufacturerIds ?? item.ManufacturerIds ?? [],
    lesterIds: item.lesterIds ?? item.Lester ?? item.LesterIds ?? [],
    applications: item.applications ?? item.Application ?? [],
  };
};

const StartersPage = () => {
  const { user } = useAuth();
  const [savedTableSettings] = useState(readStarterTableSettings);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [visibilityEditingId, setVisibilityEditingId] = useState(null);
  const [modelSuggestionsPosition, setModelSuggestionsPosition] =
    useState(null);
  const modelSuggestionsListRef = useRef(null);
  const [columnOrder, setColumnOrder] = useState(
    savedTableSettings.columnOrder,
  );
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(
    savedTableSettings.visibleColumnKeys,
  );
  const [columnFilters, setColumnFilters] = useState(
    savedTableSettings.columnFilters,
  );
  const [sortConfig, setSortConfig] = useState(savedTableSettings.sortConfig);
  const [visibilityFilter, setVisibilityFilter] = useState("ALL");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] =
    useState(false);
  const [editingStarter, setEditingStarter] = useState(null);
  const [selectedVisibility, setSelectedVisibility] = useState("DRAFT");
  const [isVisibilityPickerOpen, setIsVisibilityPickerOpen] = useState(false);
  const visibilityPickerRef = useRef(null);
  const visibilityDropdownRef = useRef(null);
  const visibilityButtonRef = useRef(null);
  const [visibilityDropdownPosition, setVisibilityDropdownPosition] =
    useState(null);
  const [model, setModel] = useState("");
  const [voltage, setVoltage] = useState(12);
  const [power, setPower] = useState(1.1);
  const [teeth, setTeeth] = useState("");
  const [rotationDirection, setRotationDirection] = useState("CW");
  const [oem, setOem] = useState("");
  const [oemIds, setOemIds] = useState([""]);
  const [manufacturer, setManufacturer] = useState("");
  const [manufacturerIds, setManufacturerIds] = useState([""]);
  const [lesterIds, setLesterIds] = useState([""]);
  const [applications, setApplications] = useState([""]);
  const [imagePaths, setImagePaths] = useState({});
  const [selectedImages, setSelectedImages] = useState([]);
  const [removeImage, setRemoveImage] = useState(false);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [imageOrder, setImageOrder] = useState([]);
  const [pendingImageUploads, setPendingImageUploads] = useState([]);
  const [imageUploadError, setImageUploadError] = useState("");
  const [isRetryingImageUpload, setIsRetryingImageUpload] = useState(false);
  const fileInputRef = useRef(null);
  const importFileInputRef = useRef(null);
  const modelInputRef = useRef(null);
  const validationMessageRef = useRef(null);

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [hasAttemptedCreate, setHasAttemptedCreate] = useState(false);
  const [deletingStarterId, setDeletingStarterId] = useState(null);
  const [starterPendingDelete, setStarterPendingDelete] = useState(null);
  const confirmDeleteStarterRef = useRef(null);
  const [deleteError, setDeleteError] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState("");

  useEffect(() => {
    if (!isVisibilityPickerOpen) return undefined;

    const closeOnOutsidePointerDown = (event) => {
      if (
        !visibilityPickerRef.current?.contains(event.target) &&
        !visibilityDropdownRef.current?.contains(event.target)
      ) {
        setIsVisibilityPickerOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
  }, [isVisibilityPickerOpen]);

  useEffect(() => {
    if (visibilityEditingId === null) return undefined;

    const closeOnOutsidePointerDown = (event) => {
      if (
        !visibilityPickerRef.current?.contains(event.target) &&
        !visibilityDropdownRef.current?.contains(event.target)
      ) {
        setVisibilityEditingId(null);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
  }, [visibilityEditingId]);

  useEffect(() => {
    if (visibilityEditingId === null) {
      setVisibilityDropdownPosition(null);
      return undefined;
    }
    const updatePosition = () => {
      const rect = visibilityButtonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 256;
      setVisibilityDropdownPosition({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        top: rect.bottom + 4,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [visibilityEditingId]);

  useEffect(() => {
    let isCurrent = true;

    getStarters()
      .then((starters) => {
        if (!isCurrent) return;

        setItems(starters.map(toStarterPageItem));
        setLoadError("");
      })
      .catch((error) => {
        if (isCurrent) {
          setLoadError(error.message || "Could not load starters.");
        }
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [reloadKey]);

  useEffect(() => {
    if (isCreateDialogOpen) modelInputRef.current?.focus();
  }, [isCreateDialogOpen]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        STARTER_TABLE_SETTINGS_KEY,
        JSON.stringify({
          columnOrder,
          visibleColumnKeys,
          columnFilters,
          sortConfig,
        }),
      );
    } catch {
      // Keep the table usable when browser storage is unavailable.
    }
  }, [columnOrder, visibleColumnKeys, columnFilters, sortConfig]);

  const modelFilter = (columnFilters.Model || "").trim().toLowerCase();
  const suggestions = modelFilter
    ? items
        .filter((item) => item.Model.toLowerCase().includes(modelFilter))
        .slice(0, 8)
    : [];
  useEffect(() => {
    if (activeSuggestionIndex >= 0) {
      modelSuggestionsListRef.current?.children[
        activeSuggestionIndex
      ]?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [activeSuggestionIndex, suggestions.length]);

  const filteredItems = items.filter(
    (item) =>
      (visibilityFilter === "ALL" || item.visibility === visibilityFilter) &&
      STARTER_COLUMNS.every(({ key }) => {
        if (key === "Image") {
          const imageFilter = columnFilters[key] || "";
          if (imageFilter === "has-image") return item.hasImage === "✅";
          if (imageFilter === "no-image") return item.hasImage !== "✅";
          return true;
        }

        const filter = columnFilters[key]?.trim().toLowerCase();
        if (!filter) return true;

        const value = getStarterColumnValue(item, key);
        const searchableValue = Array.isArray(value)
          ? value.join(" ")
          : value ?? "";
        return String(searchableValue).toLowerCase().includes(filter);
      }),
  );
  const sortedItems = [...filteredItems].sort((first, second) => {
    const firstValue = getStarterColumnValue(first, sortConfig.key);
    const secondValue = getStarterColumnValue(second, sortConfig.key);
    const firstText = Array.isArray(firstValue)
      ? firstValue.join(" ")
      : firstValue ?? "";
    const secondText = Array.isArray(secondValue)
      ? secondValue.join(" ")
      : secondValue ?? "";
    const comparison = String(firstText).localeCompare(
      String(secondText),
      undefined,
      {
        numeric: true,
        sensitivity: "base",
      },
    );
    return sortConfig.direction === "asc" ? comparison : -comparison;
  });
  const orderedColumns = columnOrder
    .filter((key) => visibleColumnKeys.includes(key))
    .map((key) => STARTER_COLUMNS.find((column) => column.key === key));

  const sortByColumn = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const reorderColumns = (event, targetKey) => {
    event.preventDefault();
    const sourceKey = event.dataTransfer.getData("text/plain");
    if (!sourceKey || sourceKey === targetKey) return;

    setColumnOrder((currentOrder) => {
      const nextOrder = [...currentOrder];
      const sourceIndex = nextOrder.indexOf(sourceKey);
      const targetIndex = nextOrder.indexOf(targetKey);
      if (sourceIndex < 0 || targetIndex < 0) return currentOrder;
      nextOrder.splice(sourceIndex, 1);
      nextOrder.splice(targetIndex, 0, sourceKey);
      return nextOrder;
    });
  };

  const openCreateDialog = () => {
    setEditingStarter(null);
    setSelectedVisibility("DRAFT");
    setIsVisibilityPickerOpen(false);
    setModel("");
    setVoltage(12);
    setPower(1.1);
    setTeeth("");
    setRotationDirection("CW");
    setOem("");
    setOemIds([""]);
    setManufacturer("");
    setManufacturerIds([""]);
    setLesterIds([""]);
    setApplications([""]);
    setSelectedImages([]);
    setRemovedImageIds([]);
    setImageOrder([]);
    setRemoveImage(false);
    resetFileInput();
    setCreateError("");
    setHasAttemptedCreate(false);
    setIsDiscardConfirmationOpen(false);
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (item) => {
    const [firstOem = "", ...restOems] = item.OEM || [];
    const separator = firstOem.indexOf(": ");
    setEditingStarter(item);
    setSelectedVisibility(item.visibility || "PUBLISHED");
    setIsVisibilityPickerOpen(false);
    setImageOrder((item.images || []).map((image) => image.imageId));
    setModel(item.Model || "");
    setVoltage(item.Voltage || 12);
    setPower(item.Power || 1.1);
    setTeeth(item.Teeth ?? "");
    setRotationDirection(item.RotationDirection ?? "CW");
    setOem(separator < 0 ? firstOem : firstOem.slice(0, separator));
    setOemIds(
      [
        ...(separator < 0 ? [] : firstOem.slice(separator + 2).split(", ")),
        ...restOems,
      ].filter(Boolean).length
        ? [
            ...(separator < 0 ? [] : firstOem.slice(separator + 2).split(", ")),
            ...restOems,
          ]
        : [""],
    );
    setManufacturer(item.Manufacturer || "");
    setManufacturerIds(
      item.ManufacturerIds?.length ? item.ManufacturerIds : [""],
    );
    setLesterIds(item.Lester?.length ? item.Lester : [""]);
    setApplications(item.Application?.length ? item.Application : [""]);
    setSelectedImages([]);
    setRemovedImageIds([]);
    setRemoveImage(false);
    resetFileInput();
    setCreateError("");
    setHasAttemptedCreate(false);
    setIsDiscardConfirmationOpen(false);
    setIsCreateDialogOpen(true);
  };

  const resetEditDialog = () => {
    if (editingStarter) openEditDialog(editingStarter);
  };

  const handleChangeModel = (event) => {
    setModel(event.target.value);
  };
  const handleChangeVoltage = (event) => {
    setVoltage(event.target.value);
  };
  const handleChangePower = (event) => {
    setPower(event.target.value);
  };
  const handleChangeTeeth = (event) => {
    setTeeth(event.target.value);
  };
  const handleChangeRotationDirection = (event) => {
    setRotationDirection(event.target.value);
  };
  const handleChangeOem = (event) => {
    setOem(event.target.value);
  };
  const handleChangeManufacturer = (event) => {
    setManufacturer(event.target.value);
  };
  const handleEditImage = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const currentImages = (editingStarter?.images || []).filter(
      (image) => !removedImageIds.includes(image.imageId),
    );
    const available = Math.max(
      0,
      5 - currentImages.length - selectedImages.length,
    );
    const acceptedFiles = files.slice(0, available);
    if (acceptedFiles.length < files.length) {
      setImageUploadError(
        "A product can have up to five images. Some selected files were not added.",
      );
    } else {
      setImageUploadError("");
    }
    const accepted = acceptedFiles.map((file) => ({
      id: `draft-${crypto.randomUUID()}`,
      file,
    }));
    setSelectedImages((current) => [...current, ...accepted]);
    setImageOrder((order) => [...order, ...accepted.map((image) => image.id)]);
    setImagePaths((current) => ({
      ...current,
      ...Object.fromEntries(acceptedFiles.map((file) => [file.name, file])),
    }));
    setRemoveImage(false);
    event.target.value = "";
  };
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleDownload = () => {
    const zip = new JSZip();
    const updatedItems = items.map((item) => {
      return Object.keys(item).reduce((acc, key) => {
        if (item.hasOwnProperty("hasImage")) {
          item.hasImage = item.hasImage
            .replace("✅", true)
            .replace("❌", false);
        }
        acc[key] = item[key];
        return acc;
      }, {});
    });
    const jsonData = JSON.stringify(updatedItems, null, 2);
    zip.file(`products-starters.json`, jsonData);
    const imagePromises = Object.entries(imagePaths).map(([filename, file]) => {
      const blob = new Blob([file]);
      zip.file(filename, blob);
      return Promise.resolve();
    });

    Promise.all(imagePromises)
      .then(() => {
        zip.generateAsync({ type: "blob" }).then((content) => {
          saveAs(content, `starter_data_${Date.now()}.zip`);
        });
      })
      .catch((error) => {
        console.error("Error adding image files to zip:", error);
      });
  };

  const handleAddItem = async () => {
    setHasAttemptedCreate(true);
    setIsCreating(true);
    setCreateError("");

    if (!model.trim() || !manufacturer.trim()) {
      setIsCreating(false);
      return;
    }

    const imageFilesToUpload = selectedImages;

    const oemIdList = filledEntries(oemIds);
    const oemEntries = oem.trim()
      ? [`${oem.trim()}${oemIdList.length ? `: ${oemIdList.join(", ")}` : ""}`]
      : oemIdList;

    try {
      const starterRequest = {
        model: model.trim(),
        voltage: String(voltage),
        power: String(power),
        teeth: teeth === "" ? null : Number(teeth),
        rotationDirection,
        oems: oemEntries,
        manufacturer: manufacturer.trim(),
        manufacturerIds: filledEntries(manufacturerIds),
        lesterIds: filledEntries(lesterIds),
        applications: filledEntries(applications),
      };
      const createdStarter = editingStarter
        ? await updateStarter(editingStarter.starterId, starterRequest)
        : await createStarter(starterRequest);

      let savedStarter = createdStarter;
      if (editingStarter && (removeImage || removedImageIds.length)) {
        try {
          const idsToDelete = removeImage
            ? (editingStarter.images || []).map((image) => image.imageId)
            : removedImageIds;
          for (const imageId of idsToDelete) {
            savedStarter = await deleteStarterImage(
              editingStarter.starterId,
              imageId,
            );
          }
        } catch (error) {
          const updatedItem = toStarterPageItem(createdStarter);
          setItems((currentItems) =>
            currentItems.map((item) =>
              item.starterId === editingStarter.starterId ? updatedItem : item,
            ),
          );
          setEditingStarter(updatedItem);
          setCreateError(error.message || "Could not remove the image.");
          setIsCreating(false);
          return;
        }
      }
      const draftImageIds = Object.fromEntries(
        imageFilesToUpload
          .filter((draft) => draft.imageId)
          .map((draft) => [draft.id, draft.imageId]),
      );
      for (const draft of imageFilesToUpload) {
        if (
          draft.imageId &&
          savedStarter.images?.some((image) => image.imageId === draft.imageId)
        )
          continue;
        try {
          savedStarter = await uploadStarterImage(
            createdStarter.starterId,
            draft.file,
            draft.file.name,
          );
          const uploadedImage =
            savedStarter.images?.[savedStarter.images.length - 1];
          if (uploadedImage) {
            draftImageIds[draft.id] = uploadedImage.imageId;
            setSelectedImages((current) =>
              current.map((entry) =>
                entry.id === draft.id
                  ? { ...entry, imageId: uploadedImage.imageId }
                  : entry,
              ),
            );
          }
          setPendingImageUploads((pending) =>
            pending.filter((entry) => entry.draftId !== draft.id),
          );
        } catch (error) {
          if (editingStarter) {
            const updatedItem = toStarterPageItem(savedStarter);
            setItems((currentItems) =>
              currentItems.map((item) =>
                item.starterId === editingStarter.starterId
                  ? updatedItem
                  : item,
              ),
            );
            setEditingStarter(updatedItem);
            setCreateError(error.message || "Could not upload the image.");
            setIsCreating(false);
            return;
          }
          setPendingImageUploads((pending) => [
            ...pending,
            {
              starterId: createdStarter.starterId,
              model: createdStarter.model,
              image: draft.file,
              draftId: draft.id,
              fileName: draft.file.name,
              order: imageOrder,
            },
          ]);
          setImageUploadError(error.message || "Could not upload the image.");
        }
      }
      const finalImageIds = orderedImageKeys
        .map((key) =>
          savedStarter.images?.some((image) => image.imageId === key)
            ? key
            : draftImageIds[key],
        )
        .filter(
          (imageId, index, imageIds) =>
            imageId &&
            imageIds.indexOf(imageId) === index &&
            savedStarter.images?.some((image) => image.imageId === imageId),
        );
      if (finalImageIds.length > 1) {
        savedStarter = await reorderStarterImages(
          createdStarter.starterId,
          finalImageIds,
        );
      }

      const currentVisibility =
        createdStarter.visibility ||
        (editingStarter ? editingStarter.visibility : "DRAFT");
      if (selectedVisibility !== currentVisibility) {
        try {
          savedStarter = await updateStarterVisibility(
            createdStarter.starterId,
            selectedVisibility,
          );
        } catch (error) {
          if (!editingStarter) {
            const savedItem = toStarterPageItem(savedStarter);
            setItems((currentItems) =>
              [...currentItems, savedItem].sort((a, b) =>
                a.Model.localeCompare(b.Model),
              ),
            );
            setEditingStarter(savedItem);
          }
          throw error;
        }
      }

      setItems((currentItems) =>
        (editingStarter
          ? currentItems.map((item) =>
              item.starterId === editingStarter.starterId
                ? toStarterPageItem(savedStarter)
                : item,
            )
          : [...currentItems, toStarterPageItem(savedStarter)]
        ).sort((a, b) => a.Model.localeCompare(b.Model)),
      );
    } catch (error) {
      setCreateError(
        error.message ||
          `Could not ${editingStarter ? "update" : "create"} starter.`,
      );
      setIsCreating(false);
      return;
    }

    setModel("");
    setVoltage(12);
    setPower(1.1);
    setTeeth("");
    setRotationDirection("CW");
    setOem("");
    setOemIds([""]);
    setManufacturer("");
    setManufacturerIds([""]);
    setLesterIds([""]);
    setApplications([""]);
    setSelectedImages([]);
    setRemovedImageIds([]);
    setImageOrder([]);
    setRemoveImage(false);
    resetFileInput();
    setIsCreating(false);
    setHasAttemptedCreate(false);
    setIsCreateDialogOpen(false);
    setIsVisibilityPickerOpen(false);
  };

  const handleVisibilityChange = async (item, visibility) => {
    try {
      const updated = await updateStarterVisibility(item.starterId, visibility);
      setItems((current) =>
        current.map((candidate) =>
          candidate.starterId === item.starterId
            ? toStarterPageItem(updated)
            : candidate,
        ),
      );
      setVisibilityEditingId(null);
    } catch (error) {
      setLoadError(error.message || "Could not update starter visibility.");
    }
  };

  const retryImageUpload = async () => {
    if (!pendingImageUploads.length || isRetryingImageUpload) return;

    setIsRetryingImageUpload(true);
    try {
      const stillPending = [];
      let updatedStarter;
      for (const pending of pendingImageUploads) {
        try {
          updatedStarter = await uploadStarterImage(
            pending.starterId,
            pending.image,
            pending.fileName,
          );
        } catch {
          stillPending.push(pending);
        }
      }
      if (updatedStarter) {
        setItems((currentItems) =>
          currentItems.map((item) =>
            item.starterId === updatedStarter.starterId
              ? toStarterPageItem(updatedStarter)
              : item,
          ),
        );
      }
      setPendingImageUploads(stillPending);
      setImageUploadError(
        stillPending.length
          ? "Some images could not be uploaded. Retry to try again."
          : "",
      );
    } catch (error) {
      setImageUploadError(error.message || "Could not upload the image.");
    } finally {
      setIsRetryingImageUpload(false);
    }
  };

  const handleDeleteStarter = async () => {
    const starter = starterPendingDelete;
    if (!starter?.starterId || deletingStarterId) return;

    setDeletingStarterId(starter.starterId);
    setDeleteError("");
    try {
      await deleteStarter(starter.starterId);
      setItems((current) =>
        current.filter((item) => item.starterId !== starter.starterId),
      );
      if (
        pendingImageUploads.some(
          (pending) => pending.starterId === starter.starterId,
        )
      ) {
        setPendingImageUploads((pending) =>
          pending.filter((entry) => entry.starterId !== starter.starterId),
        );
        setImageUploadError("");
      }
      setStarterPendingDelete(null);
      setNotice("Starter deleted.");
    } catch (error) {
      setDeleteError(error.message || `Could not delete ${starter.Model}.`);
    } finally {
      setDeletingStarterId(null);
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setIsImporting(true);
    setImportError("");
    setImportResult("");

    try {
      const importedItems = JSON.parse(await file.text());
      if (!Array.isArray(importedItems)) {
        throw new Error("The JSON file must contain an array of starters.");
      }

      const starters = importedItems.map(toCreateStarterRequest);
      const result = await importStarters(starters);

      setItems((currentItems) => {
        const merged = new Map(
          currentItems.map((item) => [item.starterId || item.Model, item]),
        );
        result.created.forEach((starter) => {
          const pageItem = toStarterPageItem(starter);
          merged.set(pageItem.starterId, pageItem);
        });
        return [...merged.values()].sort((a, b) =>
          a.Model.localeCompare(b.Model),
        );
      });

      const createdCount = result.created.length;
      const skippedModels = result.skippedModels || [];
      const skippedSummary = skippedModels.length
        ? ` Skipped existing models: ${skippedModels.join(", ")}.`
        : "";
      setImportResult(
        `Imported ${createdCount} starter${
          createdCount === 1 ? "" : "s"
        }.${skippedSummary}`,
      );
    } catch (error) {
      const validationDetails = error.errors
        ? Object.entries(error.errors)
            .map(([field, detail]) => `${field}: ${detail}`)
            .join("; ")
        : "";
      setImportError(
        [error.detail || error.message, validationDetails]
          .filter(Boolean)
          .join(" — ") || "Could not import starters.",
      );
      try {
        const starters = await getStarters();
        setItems(starters.map(toStarterPageItem));
      } catch {
        // Keep the current list if it cannot be refreshed after the failed import.
      }
    } finally {
      setIsImporting(false);
    }
  };

  const modelIsInvalid = hasAttemptedCreate && !model.trim();
  const manufacturerIsInvalid = hasAttemptedCreate && !manufacturer.trim();
  useEffect(() => {
    if (hasAttemptedCreate && (modelIsInvalid || manufacturerIsInvalid)) {
      validationMessageRef.current?.scrollIntoView({ block: "nearest" });
    }
  }, [hasAttemptedCreate, modelIsInvalid, manufacturerIsInvalid]);

  const editedOemIds = filledEntries(oemIds);
  const editedOems = oem.trim()
    ? [
        `${oem.trim()}${
          editedOemIds.length ? `: ${editedOemIds.join(", ")}` : ""
        }`,
      ]
    : editedOemIds;
  const editHasChanges =
    editingStarter &&
    (model.trim() !== editingStarter.Model ||
      String(voltage) !== String(editingStarter.Voltage) ||
      String(power) !== String(editingStarter.Power) ||
      String(teeth) !== String(editingStarter.Teeth ?? "") ||
      rotationDirection !== (editingStarter.RotationDirection ?? "CW") ||
      manufacturer.trim() !== editingStarter.Manufacturer ||
      JSON.stringify(editedOems) !== JSON.stringify(editingStarter.OEM || []) ||
      JSON.stringify(filledEntries(manufacturerIds)) !==
        JSON.stringify(editingStarter.ManufacturerIds || []) ||
      JSON.stringify(filledEntries(lesterIds)) !==
        JSON.stringify(editingStarter.Lester || []) ||
      JSON.stringify(filledEntries(applications)) !==
        JSON.stringify(editingStarter.Application || []) ||
      selectedImages.length > 0 ||
      removedImageIds.length > 0 ||
      JSON.stringify(imageOrder) !==
        JSON.stringify(
          (editingStarter.images || []).map((image) => image.imageId),
        ) ||
      removeImage ||
      selectedVisibility !== (editingStarter.visibility || "PUBLISHED"));
  const hasUnsavedChanges = editingStarter
    ? Boolean(editHasChanges)
    : Boolean(
        model.trim() ||
          manufacturer.trim() ||
          String(voltage) !== "12" ||
          String(power) !== "1.1" ||
          teeth !== "" ||
          rotationDirection !== "CW" ||
          oem.trim() ||
          filledEntries(oemIds).length ||
          filledEntries(manufacturerIds).length ||
          filledEntries(lesterIds).length ||
          filledEntries(applications).length ||
          selectedImages.length ||
          selectedVisibility !== "DRAFT",
      );
  const finishCloseCreateDialog = () => {
    setIsDiscardConfirmationOpen(false);
    setIsCreateDialogOpen(false);
    setCreateError("");
    setHasAttemptedCreate(false);
  };
  const closeCreateDialog = () => {
    if (isCreating) return;
    if (hasUnsavedChanges) {
      setIsDiscardConfirmationOpen(true);
      return;
    }
    finishCloseCreateDialog();
  };
  const activeSavedImages = (editingStarter?.images || [])
    .filter((image) => !removedImageIds.includes(image.imageId) && !removeImage)
    .slice();
  const savedImagesById = new Map(
    activeSavedImages.map((image) => [image.imageId, image]),
  );
  const draftImagesById = new Map(
    selectedImages.map((image) => [image.id, image]),
  );
  const orderedImageKeys = [
    ...imageOrder,
    ...activeSavedImages
      .map((image) => image.imageId)
      .filter((id) => !imageOrder.includes(id)),
    ...selectedImages
      .map((image) => image.id)
      .filter((id) => !imageOrder.includes(id)),
  ];
  const orderedImageItems = orderedImageKeys
    .map((key) => ({
      key,
      saved: savedImagesById.get(key),
      draft: draftImagesById.get(key),
    }))
    .filter((item) => item.saved || item.draft);
  const productImageItems = orderedImageItems.map(({ key, saved, draft }) => {
    const uploadedImage = draft?.imageId
      ? (editingStarter?.images || []).find(
          (image) => image.imageId === draft.imageId,
        )
      : null;
    const previewImage = saved || uploadedImage;
    return {
      key,
      name: saved?.imageName || draft.file.name,
      src: previewImage?.imageUrl,
      alt: `Starter ${editingStarter?.Model || model}`,
      status: draft?.imageId ? "Uploaded" : draft ? "Ready to upload" : "Saved",
      source: { key, saved, draft },
    };
  });

  return (
    <ProductCatalogPage
      title="Starters"
      description="Search, review, and maintain product specifications."
    >
      {(hasPermission(user, "STARTERS_READ") ||
        hasPermission(user, "STARTERS_CREATE")) && (
        <ProductCatalogToolbar>
          {hasPermission(user, "STARTERS_CREATE") && (
            <ProductCatalogActionButton
              variant="primary"
              label="Create starter"
              title="Create starter"
              onClick={openCreateDialog}
              disabled={
                isLoading ||
                Boolean(loadError) ||
                Boolean(pendingImageUploads.length)
              }
              icon="/icons/create-icon.svg"
            >
              Create starter
            </ProductCatalogActionButton>
          )}
          {isRootUser(user) && hasPermission(user, "STARTERS_READ") && (
            <ProductCatalogActionButton
              label="Download data"
              title="Download data"
              disabled={items.length === 0}
              onClick={handleDownload}
              icon="/icons/download-icon.svg"
            >
              Export data
            </ProductCatalogActionButton>
          )}
          {isRootUser(user) && hasPermission(user, "STARTERS_CREATE") && (
            <input
              type="file"
              className="hidden"
              accept=".json"
              onChange={handleImportFile}
              ref={importFileInputRef}
              disabled={isImporting || isLoading}
            />
          )}
          {isRootUser(user) && hasPermission(user, "STARTERS_CREATE") && (
            <ProductCatalogActionButton
              label="Import JSON"
              title="Import JSON"
              disabled={isImporting || isLoading}
              onClick={() => importFileInputRef.current?.click()}
              icon="/icons/import-icon.svg"
            >
              Import JSON
            </ProductCatalogActionButton>
          )}
        </ProductCatalogToolbar>
      )}

      {importError && (
        <DismissibleAlert onDismiss={() => setImportError("")}>
          Could not import starters: {importError}
        </DismissibleAlert>
      )}
      {importResult && (
        <DismissibleAlert
          variant="success"
          onDismiss={() => setImportResult("")}
        >
          {importResult}
        </DismissibleAlert>
      )}
      {notice && (
        <DismissibleAlert variant="success" onDismiss={() => setNotice("")}>
          {notice}
        </DismissibleAlert>
      )}
      {deleteError && (
        <p role="alert" className="mb-4 rounded bg-red-100 p-3 text-red-800">
          Could not delete starter: {deleteError}
        </p>
      )}
      {starterPendingDelete && (
        <ProductDeleteConfirmationDialog
          productName="starter"
          model={starterPendingDelete.Model}
          error={deleteError}
          deleting={deletingStarterId !== null}
          onCancel={() => {
            setStarterPendingDelete(null);
            setDeleteError("");
          }}
          onConfirm={handleDeleteStarter}
          confirmButtonRef={confirmDeleteStarterRef}
        />
      )}
      {pendingImageUploads.length > 0 && (
        <div role="alert" className="mb-4 rounded bg-red-100 p-3 text-red-800">
          <p>
            {pendingImageUploads.length} image(s) could not be uploaded:{" "}
            {imageUploadError}
          </p>
          <button
            type="button"
            onClick={retryImageUpload}
            disabled={isRetryingImageUpload}
            className="mt-2 rounded bg-red-800 px-3 py-1 text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRetryingImageUpload ? "Uploading…" : "Retry image upload"}
          </button>
        </div>
      )}

      {isLoading && <p role="status">Loading starters…</p>}
      {!isLoading && loadError && (
        <div role="alert" className="mb-4 rounded bg-red-100 p-3 text-red-800">
          <p>Could not load starters: {loadError}</p>
          <button
            type="button"
            className="mt-2 rounded bg-red-800 px-3 py-1 text-white"
            onClick={() => {
              setIsLoading(true);
              setReloadKey((key) => key + 1);
            }}
          >
            Try again
          </button>
        </div>
      )}
      {!isLoading && !loadError && (
        <>
          <ProductCatalogSectionHeader
            description="Filter in the column headers; select a header to sort."
            controls={
              <>
                <ProductCatalogTableControls
                  visibilityFilter={visibilityFilter}
                  onVisibilityFilterChange={setVisibilityFilter}
                  onReset={() => {
                    setColumnFilters({});
                    setVisibilityFilter("ALL");
                    setSortConfig({ key: "Model", direction: "asc" });
                  }}
                  columns={STARTER_COLUMNS}
                  visibleColumnKeys={visibleColumnKeys}
                  onVisibleColumnKeysChange={setVisibleColumnKeys}
                />
              </>
            }
          />
          <ProductCatalogTableFrame label="Starter list">
            <table className={productCatalogFlexibleTableClassName}>
              <thead className="bg-gray-50">
                <tr>
                  {orderedColumns.map(({ key, label }) => (
                    <ProductCatalogSortableHeader
                      key={key}
                      columnKey={key}
                      label={label}
                      sortConfig={sortConfig}
                      onSort={sortByColumn}
                      widthClass={STARTER_COLUMN_WIDTHS[key]}
                      onDropColumn={reorderColumns}
                      filterId={`starter-filter-${key}`}
                    >
                      {key === "Model" ? (
                        <div className="relative">
                          <input
                            id="starter-filter-Model"
                            type="search"
                            role="combobox"
                            aria-autocomplete="list"
                            aria-controls="starter-model-suggestions"
                            aria-activedescendant={
                              activeSuggestionIndex >= 0 &&
                              suggestions[activeSuggestionIndex]
                                ? `starter-suggestion-${
                                    suggestions[activeSuggestionIndex]
                                      .starterId || activeSuggestionIndex
                                  }`
                                : undefined
                            }
                            aria-expanded={
                              suggestions.length > 0 &&
                              activeSuggestionIndex >= 0
                            }
                            autoComplete="off"
                            value={columnFilters.Model || ""}
                            onFocus={(event) => {
                              if (suggestions.length)
                                setActiveSuggestionIndex(0);
                              const bounds =
                                event.currentTarget.getBoundingClientRect();
                              setModelSuggestionsPosition({
                                left: Math.min(
                                  bounds.left,
                                  window.innerWidth - 240,
                                ),
                                top: bounds.bottom + 4,
                                width: Math.max(bounds.width, 240),
                              });
                            }}
                            onBlur={() => setActiveSuggestionIndex(-1)}
                            onChange={(event) => {
                              const value = event.target.value;
                              setColumnFilters((current) => ({
                                ...current,
                                Model: value,
                              }));
                              setActiveSuggestionIndex(0);
                              const bounds =
                                event.currentTarget.getBoundingClientRect();
                              setModelSuggestionsPosition({
                                left: Math.min(
                                  bounds.left,
                                  window.innerWidth - 240,
                                ),
                                top: bounds.bottom + 4,
                                width: Math.max(bounds.width, 240),
                              });
                            }}
                            onKeyDown={(event) => {
                              if (
                                event.key === "ArrowDown" &&
                                suggestions.length
                              ) {
                                event.preventDefault();
                                setActiveSuggestionIndex(
                                  (index) => (index + 1) % suggestions.length,
                                );
                              } else if (
                                event.key === "ArrowUp" &&
                                suggestions.length
                              ) {
                                event.preventDefault();
                                setActiveSuggestionIndex((index) =>
                                  index <= 0
                                    ? suggestions.length - 1
                                    : index - 1,
                                );
                              } else if (
                                event.key === "Enter" &&
                                suggestions[activeSuggestionIndex]
                              ) {
                                event.preventDefault();
                                setColumnFilters((current) => ({
                                  ...current,
                                  Model:
                                    suggestions[activeSuggestionIndex].Model,
                                }));
                                setActiveSuggestionIndex(-1);
                              } else if (event.key === "Escape") {
                                setActiveSuggestionIndex(-1);
                              }
                            }}
                            placeholder="Filter…"
                            className="mt-2 w-full min-w-0 rounded border border-gray-300 px-1.5 py-1 text-xs font-normal focus:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
                          />
                          {suggestions.length > 0 &&
                            activeSuggestionIndex >= 0 &&
                            modelSuggestionsPosition && (
                              <ul
                                id="starter-model-suggestions"
                                ref={modelSuggestionsListRef}
                                role="listbox"
                                className="fixed z-50 max-h-56 overflow-y-auto rounded border border-gray-200 bg-white shadow-lg"
                                style={modelSuggestionsPosition}
                              >
                                {suggestions.map((item, index) => (
                                  <li
                                    id={`starter-suggestion-${
                                      item.starterId || index
                                    }`}
                                    key={item.starterId || item.Model}
                                    role="option"
                                    aria-selected={
                                      activeSuggestionIndex === index
                                    }
                                    onMouseEnter={() =>
                                      setActiveSuggestionIndex(index)
                                    }
                                    onMouseDown={(event) =>
                                      event.preventDefault()
                                    }
                                    onClick={() => {
                                      setColumnFilters((current) => ({
                                        ...current,
                                        Model: item.Model,
                                      }));
                                      setActiveSuggestionIndex(-1);
                                    }}
                                    className={`cursor-pointer px-3 py-2 text-left ${
                                      activeSuggestionIndex === index
                                        ? "bg-blue-100 text-blue-900"
                                        : "hover:bg-blue-50"
                                    }`}
                                  >
                                    {item.Model}
                                  </li>
                                ))}
                              </ul>
                            )}
                        </div>
                      ) : key === "Image" ? (
                        <ProductCatalogColumnFilter
                          id={`starter-filter-${key}`}
                          label={label}
                          value={columnFilters[key] || ""}
                          onChange={(value) =>
                            setColumnFilters((current) => ({
                              ...current,
                              [key]: value,
                            }))
                          }
                          options={[
                            { value: "has-image", label: "Has image" },
                            { value: "no-image", label: "No image" },
                          ]}
                        />
                      ) : (
                        <ProductCatalogColumnFilter
                          id={`starter-filter-${key}`}
                          label={label}
                          value={columnFilters[key] || ""}
                          onChange={(value) =>
                            setColumnFilters((current) => ({
                              ...current,
                              [key]: value,
                            }))
                          }
                        />
                      )}
                    </ProductCatalogSortableHeader>
                  ))}
                  <th
                    scope="col"
                    className="border-b border-gray-200 px-2 py-2 text-left align-top"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedItems.length === 0 && (
                  <ProductCatalogEmptyRow colSpan={orderedColumns.length + 1}>
                    {items.length === 0
                      ? "No starters are available yet."
                      : "No starters match your filters."}
                  </ProductCatalogEmptyRow>
                )}
                {sortedItems.map((item, index) => (
                  <tr
                    key={
                      item.starterId ||
                      `${item.Model}-${item.Voltage}-${item.Power}`
                    }
                    className="align-top hover:bg-gray-50"
                  >
                    {orderedColumns.map(({ key }) => {
                      const value = getStarterColumnValue(item, key);
                      return (
                        <td
                          key={key}
                          className="break-words px-2 py-3 text-gray-800"
                        >
                          {key === "Model" ? (
                            <div className="flex flex-col items-start gap-1">
                              <span>{value}</span>
                              {(() => {
                                const canEditVisibility =
                                  hasPermission(
                                    user,
                                    "STARTERS_VISIBILITY_MANAGE",
                                  ) ||
                                  (item.createdByUserId === user?.userId &&
                                    hasPermission(user, "STARTERS_UPDATE"));
                                const option =
                                  PRODUCT_VISIBILITY_OPTIONS.find(
                                    ({ value: state }) =>
                                      state === item.visibility,
                                  ) || PRODUCT_VISIBILITY_OPTIONS[0];
                                return (
                                  <div
                                    ref={
                                      visibilityEditingId === item.starterId
                                        ? visibilityPickerRef
                                        : null
                                    }
                                    className={`relative flex flex-col items-start gap-1 ${
                                      visibilityEditingId === item.starterId
                                        ? "z-30"
                                        : ""
                                    }`}
                                  >
                                    <button
                                      ref={
                                        visibilityEditingId === item.starterId
                                          ? visibilityButtonRef
                                          : null
                                      }
                                      type="button"
                                      aria-label={`Visibility: ${
                                        option.label
                                      } for ${item.Model}${
                                        canEditVisibility
                                          ? ". Edit visibility"
                                          : ""
                                      }`}
                                      aria-haspopup={
                                        canEditVisibility ? "true" : undefined
                                      }
                                      aria-expanded={
                                        visibilityEditingId === item.starterId
                                      }
                                      onClick={() =>
                                        canEditVisibility &&
                                        setVisibilityEditingId((current) =>
                                          current === item.starterId
                                            ? null
                                            : item.starterId,
                                        )
                                      }
                                      disabled={!canEditVisibility}
                                      title={option.description}
                                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                        item.visibility === "PUBLISHED"
                                          ? "bg-green-100 text-green-800"
                                          : "bg-amber-100 text-amber-900"
                                      } ${
                                        canEditVisibility
                                          ? "cursor-pointer hover:ring-2 hover:ring-blue-300"
                                          : "cursor-default"
                                      }`}
                                    >
                                      {option.label}
                                    </button>
                                    {visibilityEditingId === item.starterId &&
                                      canEditVisibility &&
                                      visibilityDropdownPosition &&
                                      createPortal(
                                        <div
                                          ref={visibilityDropdownRef}
                                          style={{
                                            position: "fixed",
                                            left: visibilityDropdownPosition.left,
                                            top: visibilityDropdownPosition.top,
                                            maxHeight: "calc(100vh - 16px)",
                                            overflowY: "auto",
                                          }}
                                          className="z-[1000] w-64 space-y-1 rounded-md border border-zinc-300 bg-white p-2 shadow-lg"
                                          role="group"
                                          aria-label={`Choose visibility for ${item.Model}`}
                                        >
                                          {PRODUCT_VISIBILITY_OPTIONS.map(
                                            ({
                                              value: state,
                                              label,
                                              description,
                                            }) => (
                                              <button
                                                key={state}
                                                type="button"
                                                onClick={() =>
                                                  handleVisibilityChange(
                                                    item,
                                                    state,
                                                  )
                                                }
                                                disabled={
                                                  item.visibility === state
                                                }
                                                className="block w-full rounded px-2 py-1 text-left hover:bg-zinc-100 disabled:bg-blue-50 disabled:font-semibold"
                                              >
                                                <span className="block text-xs text-zinc-900">
                                                  {label}
                                                </span>
                                                <span className="block whitespace-normal text-[10px] font-normal text-zinc-600">
                                                  {description}
                                                </span>
                                              </button>
                                            ),
                                          )}
                                        </div>,
                                        document.body,
                                      )}
                                  </div>
                                );
                              })()}
                            </div>
                          ) : key === "Image" && item.hasImage === "✅" ? (
                            <span className="flex items-center gap-1">
                              {(item.images || []).slice(0, 3).map((image) => (
                                <ProductImagePreview
                                  key={image.imageId}
                                  src={image.imageUrl}
                                  alt={`Starter ${item.Model}`}
                                />
                              ))}
                              {(item.images || []).length > 3 && (
                                <span className="text-xs">
                                  +{item.images.length - 3}
                                </span>
                              )}
                            </span>
                          ) : Array.isArray(value) ? (
                            value.length > 0 ? (
                              <ul className="flex min-w-0 flex-wrap gap-1">
                                {value.map((entry, entryIndex) => (
                                  <li
                                    key={`${entry}-${entryIndex}`}
                                    className="break-words rounded bg-gray-100 px-2 py-0.5 text-xs"
                                  >
                                    {entry}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )
                          ) : (
                            value ?? <span className="text-gray-400">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-2 py-3">
                      <div className="flex gap-1">
                        {item.starterId &&
                          hasPermission(user, "STARTERS_UPDATE") && (
                            <EditButton
                              onClick={() => openEditDialog(item)}
                              label={`Edit starter ${item.Model}`}
                              title="Edit starter"
                            />
                          )}
                        {item.starterId &&
                          hasPermission(user, "STARTERS_DELETE") && (
                            <DeleteButton
                              label={`Delete starter ${item.Model}`}
                              title="Delete starter"
                              disabled={deletingStarterId === item.starterId}
                              onClick={() => setStarterPendingDelete(item)}
                            />
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ProductCatalogTableFrame>
        </>
      )}

      {isCreateDialogOpen && (
        <>
          <ProductCatalogDialog
            productName="Starter"
            title={
              editingStarter
                ? `Edit starter ${editingStarter.Model}`
                : "Create starter"
            }
            visibility={selectedVisibility}
            onClose={closeCreateDialog}
            closeDisabled={isCreating}
          >
            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleAddItem();
              }}
              noValidate
              className="min-h-0 flex-1 grid grid-cols-1 content-start gap-x-4 gap-y-3 overflow-y-auto p-5 sm:grid-cols-12 sm:gap-y-2.5"
            >
              <div className="border-b border-zinc-200 pb-1 sm:col-span-12">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-800">
                  Specifications
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:col-span-12 sm:grid-cols-2 lg:grid-cols-[12rem_12rem_6rem_10rem_5rem_6rem]">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-zinc-700">
                    Model <span className="text-red-600">*</span>
                  </span>
                  <input
                    ref={modelInputRef}
                    type="text"
                    value={model}
                    onChange={handleChangeModel}
                    aria-required="true"
                    aria-invalid={modelIsInvalid}
                    aria-describedby={
                      modelIsInvalid ? "starter-model-error" : undefined
                    }
                    className={`w-full rounded-md border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                      modelIsInvalid
                        ? "border-red-600 focus:ring-red-200"
                        : "border-gray-300 focus:border-blue-700 focus:ring-blue-200"
                    }`}
                  />
                  {modelIsInvalid && (
                    <span
                      id="starter-model-error"
                      className="mt-1 block text-sm text-red-700"
                    >
                      Model is required.
                    </span>
                  )}
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-zinc-700">
                    Manufacturer <span className="text-red-600">*</span>
                  </span>
                  <input
                    type="text"
                    placeholder="Bosch"
                    value={manufacturer}
                    onChange={handleChangeManufacturer}
                    aria-required="true"
                    aria-invalid={manufacturerIsInvalid}
                    aria-describedby={
                      manufacturerIsInvalid
                        ? "starter-manufacturer-error"
                        : undefined
                    }
                    className={`w-full rounded-md border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                      manufacturerIsInvalid
                        ? "border-red-600 focus:ring-red-200"
                        : "border-gray-300 focus:border-blue-700 focus:ring-blue-200"
                    }`}
                  />
                  {manufacturerIsInvalid && (
                    <span
                      id="starter-manufacturer-error"
                      className="mt-1 block text-sm text-red-700"
                    >
                      Manufacturer is required.
                    </span>
                  )}
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-zinc-700">
                    Voltage
                  </span>
                  <select
                    value={voltage}
                    onChange={handleChangeVoltage}
                    className="w-full rounded-md border border-zinc-300 bg-white px-2.5 py-1.5 text-sm"
                  >
                    <option value={12}>12V</option>
                    <option value={24}>24V</option>
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 flex justify-between text-xs font-semibold text-zinc-700">
                    <span>Power</span>
                    <span>{power} kW</span>
                  </span>
                  <input
                    type="range"
                    min={0.1}
                    max={8.0}
                    step={0.1}
                    value={power}
                    onChange={handleChangePower}
                    className="mt-1 w-full"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-zinc-700">
                    Teeth
                  </span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={teeth}
                    onChange={handleChangeTeeth}
                    className="w-full rounded-md border border-zinc-300 bg-white px-2.5 py-1.5 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-zinc-700">
                    Rotation
                  </span>
                  <select
                    value={rotationDirection}
                    onChange={handleChangeRotationDirection}
                    className="w-full rounded-md border border-zinc-300 bg-white px-2.5 py-1.5 text-sm"
                  >
                    <option value="CW">CW</option>
                    <option value="CCW">CCW</option>
                  </select>
                </label>
              </div>

              <div className="mt-1 border-b border-zinc-200 pb-1 sm:col-span-12">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-800">
                  References and applications
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:col-span-12 sm:grid-cols-2 lg:grid-cols-12">
                <label className="block lg:col-span-1">
                  <span className="mb-1 block text-xs font-semibold text-zinc-700">
                    OEM
                  </span>
                  <input
                    type="text"
                    placeholder="BMW, Ford, ..."
                    value={oem}
                    onChange={handleChangeOem}
                    className="w-full rounded-md border border-zinc-300 bg-white px-2.5 py-1.5 text-sm"
                  />
                </label>
                <ProductChipListField
                  id="starter-oem-id"
                  label="OEM ids"
                  placeholder="Type an ID and press Enter"
                  values={oemIds}
                  onChange={setOemIds}
                  className="lg:col-span-3"
                />
                <ProductChipListField
                  id="starter-manufacturer-id"
                  label="Manufacturer ids"
                  placeholder="Type an ID and press Enter"
                  values={manufacturerIds}
                  onChange={setManufacturerIds}
                  className="lg:col-span-3"
                />
                <ProductChipListField
                  id="starter-lester-id"
                  label="Lester ids"
                  placeholder="Type an ID and press Enter"
                  values={lesterIds}
                  onChange={setLesterIds}
                  className="lg:col-span-2"
                />
                <ProductChipListField
                  id="starter-application"
                  label="Applications"
                  placeholder="Type an application and press Enter"
                  values={applications}
                  onChange={setApplications}
                  className="lg:col-span-3"
                />
              </div>

              <div className="sm:col-span-12">
                <ProductImageManager
                  id="starter-image"
                  images={productImageItems}
                  warning={
                    removeImage ? "Saved images will be removed on save." : ""
                  }
                  inputRef={fileInputRef}
                  onFilesSelected={handleEditImage}
                  onMove={(imageIndex, offset) =>
                    setImageOrder((order) =>
                      moveListItem(
                        order,
                        order.indexOf(productImageItems[imageIndex].key),
                        offset,
                      ),
                    )
                  }
                  onRemove={(productImage) => {
                    const { key, saved, draft } = productImage.source;
                    if (saved)
                      setRemovedImageIds((ids) => [...ids, saved.imageId]);
                    if (draft?.imageId)
                      setRemovedImageIds((ids) =>
                        ids.includes(draft.imageId)
                          ? ids
                          : [...ids, draft.imageId],
                      );
                    if (draft)
                      setSelectedImages((images) =>
                        images.filter((image) => image.id !== draft.id),
                      );
                    setImageOrder((order) =>
                      order.filter(
                        (imageId) =>
                          imageId !== key &&
                          imageId !== saved?.imageId &&
                          imageId !== draft?.imageId,
                      ),
                    );
                  }}
                />
                {removeImage && (
                  <button
                    type="button"
                    onClick={() => setRemoveImage(false)}
                    className="mt-2 rounded border border-zinc-300 px-2 py-1 text-xs text-zinc-700"
                  >
                    Undo image removal
                  </button>
                )}
              </div>

              <div className="min-h-10 sm:col-span-12">
                {createError && (
                  <p
                    role="alert"
                    className="rounded-md bg-red-50 p-2.5 text-sm text-red-800"
                  >
                    Could not {editingStarter ? "update" : "create"} starter:{" "}
                    {createError}
                  </p>
                )}
              </div>
              <div className="sticky bottom-0 -mx-5 -mb-5 mt-1 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 bg-white px-5 py-3 sm:col-span-12">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeCreateDialog}
                    disabled={isCreating}
                    className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  {editingStarter && (
                    <button
                      type="button"
                      onClick={resetEditDialog}
                      disabled={!editHasChanges || isCreating}
                      className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Reset changes
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <p
                    ref={validationMessageRef}
                    role={
                      hasAttemptedCreate &&
                      (modelIsInvalid || manufacturerIsInvalid)
                        ? "alert"
                        : undefined
                    }
                    aria-live="polite"
                    className="text-xs text-red-700"
                  >
                    {hasAttemptedCreate &&
                    (modelIsInvalid || manufacturerIsInvalid)
                      ? "Complete the required fields before saving."
                      : ""}
                  </p>
                  {(() => {
                    const canChangeVisibility =
                      hasPermission(user, "STARTERS_VISIBILITY_MANAGE") ||
                      (!editingStarter &&
                        hasPermission(user, "STARTERS_CREATE")) ||
                      (editingStarter?.createdByUserId === user?.userId &&
                        hasPermission(user, "STARTERS_UPDATE"));
                    const selectedOption = PRODUCT_VISIBILITY_OPTIONS.find(
                      ({ value }) => value === selectedVisibility,
                    );
                    return canChangeVisibility ? (
                      <div ref={visibilityPickerRef} className="relative">
                        <button
                          type="button"
                          aria-haspopup="true"
                          aria-expanded={isVisibilityPickerOpen}
                          onClick={() =>
                            setIsVisibilityPickerOpen((open) => !open)
                          }
                          disabled={isCreating}
                          className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                        >
                          Visibility:{" "}
                          {selectedOption?.label || selectedVisibility}
                        </button>
                        {isVisibilityPickerOpen && (
                          <div
                            role="group"
                            aria-label="Choose product visibility"
                            className="absolute bottom-full right-0 z-30 mb-2 w-72 space-y-1 rounded-md border border-zinc-300 bg-white p-2 shadow-lg"
                          >
                            {PRODUCT_VISIBILITY_OPTIONS.map(
                              ({ value, label, description }) => (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() => {
                                    setSelectedVisibility(value);
                                    setIsVisibilityPickerOpen(false);
                                  }}
                                  aria-pressed={selectedVisibility === value}
                                  className={`block w-full rounded px-2 py-1.5 text-left hover:bg-zinc-100 ${
                                    selectedVisibility === value
                                      ? "bg-blue-50 font-semibold"
                                      : ""
                                  }`}
                                >
                                  <span className="block text-sm text-zinc-900">
                                    {label}
                                  </span>
                                  <span className="block text-xs font-normal text-zinc-600">
                                    {description}
                                  </span>
                                </button>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    ) : null;
                  })()}
                  <button
                    type="submit"
                    disabled={isCreating || (editingStarter && !editHasChanges)}
                    className="rounded-md bg-blue-800 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-900 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    {isCreating
                      ? selectedImages.length
                        ? "Saving and uploading…"
                        : "Saving…"
                      : editingStarter
                      ? "Save changes"
                      : "Save starter"}
                  </button>
                </div>
              </div>
            </form>
          </ProductCatalogDialog>
          {isDiscardConfirmationOpen && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
              <section
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="discard-starter-title"
                aria-describedby="discard-starter-description"
                className="w-full max-w-md rounded-xl border border-zinc-200 bg-white p-5 shadow-2xl"
              >
                <h3
                  id="discard-starter-title"
                  className="text-lg font-semibold text-zinc-950"
                >
                  Discard unsaved changes?
                </h3>
                <p
                  id="discard-starter-description"
                  className="mt-2 text-sm text-zinc-600"
                >
                  Your starter changes and selected images will be lost.
                </p>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    autoFocus
                    onClick={() => setIsDiscardConfirmationOpen(false)}
                    className="rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                  >
                    Keep editing
                  </button>
                  <button
                    type="button"
                    onClick={finishCloseCreateDialog}
                    className="rounded-md bg-red-700 px-3 py-2 text-sm font-medium text-white hover:bg-red-800"
                  >
                    Discard changes
                  </button>
                </div>
              </section>
            </div>
          )}
        </>
      )}
    </ProductCatalogPage>
  );
};

export default StartersPage;
